<template>
    <div class="main-hero">
        <div class="main-hero__background">
            <video
                v-if="!videoError"
                autoplay
                muted
                loop
                playsinline
                ref="videoRef"
                @error="handleVideoError"
                @loadeddata="handleVideoLoaded"
            >
                <source src="/local/templates/z-template/videos/mainHero.mp4" type="video/mp4">
                <source src="/local/templates/z-template/videos/mainHero.webm" type="video/webm">
                Your browser does not support the video tag.
            </video>
            <z-image
                v-else
                srcset="/local/templates/z-template/images/hero/hero-main@2x.jpg 2x"
                src="/local/templates/z-template/images/hero/hero-main.jpg"
            />
        </div>
        <div class="main-hero__container container">
            <div class="main-hero__top">
                <h1 class="main-hero__title" v-html="title"></h1>
                <div class="main-hero__socials">
                    <z-link
                        v-for="link in socialList"
                        :key="link.id"
                        :href="link.link"
                        class="main-hero__social-link"
                        target="_blank"
                        rel="noopener"
                    >
                        <z-icon
                            :name="link.icon"
                            width="32"
                            height="32"
                        />
                    </z-link>
                </div>
            </div>
            <div
                v-if="newsList && newsList.length"
                class="main-hero__news"
            >
                <div class="main-hero__news-wrapper">
                    <a
                        v-for="news in newsList"
                        :key="news.code"
                        :href="news.detailPageUrl"
                        class="main-hero__news-card"
                    >
                        <z-date
                            class="main-hero__news-date"
                            :from="news.activeFrom"
                            theme="simple"
                        />
                        <span class="main-hero__news-title">{{ news.name }}</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted } from 'vue';
import {useGetter} from '@/composable/useGetter'
import {localize} from '@/utils/i18n'

const {app} = useGetter(['app'])
const newsList = app.value.components.news.items

defineProps({
    posterUrl: {
        type: String,
        default: '',
    },
    title: {
        type: String,
        default: localize({
            ru: 'Место<br/>рождения<br/>достижений',
            en: 'Where<br/>achievements<br/>are born',
            cn: '成就的发源地'
        }),
    }
})

const videoRef = ref(null);
const videoError = ref(false);

const socialList = [
    {id: 1, link: 'https://vk.com/irkutskoilcompany', icon: 'social/vk',},
    {id: 2, link: 'https://t.me/irkutskoilcompany', icon: 'social/telegram',},
    {id: 3, link: 'https://rutube.ru/channel/24315454/', icon: 'social/youtube',},
    {id: 4, link: 'https://max.ru/irkutskoilcompany', icon: 'social/max',},
]

const handleVideoError = () => {
    videoError.value = true;
};

const handleVideoLoaded = () => {
    videoError.value = false;
};

onMounted(() => {
    if (videoRef.value) {
        videoRef.value.play().catch((error) => {
            console.error('Ошибка воспроизведения:', error);
            videoError.value = true;
        });
    }
});

</script>
<style lang="scss" scoped>
.main-hero {
    position: relative;
    min-height: 100vh;
    display: flex;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: -1;

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(0deg, rgba(7, 50, 49, 0.20) 0%, rgba(7, 50, 49, 0.20) 100%);
            z-index: 1;
            pointer-events: none;
        }

        & video {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    &__container {
        padding-top: 256px;
        @include padding-level(bottom, L);
        flex: 1 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        @include gap-level(L);

        @include breakpoint(mobile){
            padding-top: 144px;
        }
    }

    &__top {
        display: flex;
        justify-content: space-between;
        @include gap-level(S);
    }

    &__title {
        @include typo-level(3XL);
        font-weight: 500;
        line-height: 105%;
        color: var(--color-white);

        @include breakpoint(mobile){
            font-size: 48px;
            line-height: 110%;
            letter-spacing: -0.48px;
        }
    }

    &__socials {
        @include breakpoint(mobile) {
            display: none;
        }
    }
    &__social-link{
        padding: 20px;
        width: fit-content;
        display: block;
        border-radius: 50%;
        background: var(--color-white);
        &:not(:last-of-type) {
            margin-bottom: var(--spacer-s);
        }

        @include breakpoint(laptop){
            padding: 12px;

            & .z-icon{
                width: 24px;
                height: 24px;
            }
        }
    }

    &__news {
        overflow: scroll;
        margin-right: -$token-grids-desktop-offset;
        margin-left: -$token-grids-desktop-offset;
        padding-right: $token-grids-desktop-offset;
        padding-left: $token-grids-desktop-offset;

        @include breakpoint(laptop) {
            margin-right: -$token-grids-laptop-offset;
            margin-left: -$token-grids-laptop-offset;
            padding-right: $token-grids-laptop-offset;
            padding-left: $token-grids-laptop-offset;
        }

        @include breakpoint(tablet) {
            margin-right: -$token-grids-tablet-offset;
            margin-left: -$token-grids-tablet-offset;
            padding-right: $token-grids-tablet-offset;
            padding-left: $token-grids-tablet-offset;
        }

        @include breakpoint(v-tablet) {
            margin-right: -$token-grids-v-tablet-offset;
            margin-left: -$token-grids-v-tablet-offset;
            padding-right: $token-grids-v-tablet-offset;
            padding-left: $token-grids-v-tablet-offset;
            max-width: calc(100%  + $token-grids-v-tablet-offset * 2);
        }

        @include breakpoint(mobile) {
            margin-right: -$token-grids-mobile-offset;
            margin-left: -$token-grids-mobile-offset;
            padding-right: $token-grids-mobile-offset;
            padding-left: $token-grids-mobile-offset;
            max-width: calc(100%  + $token-grids-mobile-offset * 2);

        }

        &::-webkit-scrollbar {
            display: none;
        }

        &-wrapper {
            display: flex;
            gap: 15px;

            width: 100%;

            @include breakpoint(v-tablet){
                min-width: max-content;
            }
        }

        &-card {
            @include padding-level(top, XS);
            @include padding-level(right, M);
            @include padding-level(bottom, XS);
            @include padding-level(left, M);
            flex: 1 1;
            min-width: 293px;
            max-width: 550px;
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.20);
            background-color: rgba(2, 105, 88, 0.40);
            backdrop-filter: blur(20px);
            text-decoration: unset;
            transition: background-color .4s ease-in-out;

            @include breakpoint(mobile){
                max-width: calc(100vw - ($token-grids-mobile-offset * 2))
            }

            &:hover {
                background-color: rgba(2, 105, 88, 0.85);

            }

            & a {
                text-decoration: none;
            }
        }

        &-date {
            @include margin-level(bottom, 2XS);

            &:deep() {
                & .z-date__day, & .z-date__other {
                    color: var(--color-white);
                    line-height: 150%;

                    @include breakpoint(mobile) {
                        font-size: 14px;
                    }
                }
            }
        }

        &-title {
            @include typo-level(L);
            color: var(--color-white);
            line-height: 160%;
            letter-spacing: 0.23px;

            @include breakpoint(mobile) {
                font-size: 16px;
            }
        }
    }
}
</style>
